/**
 * Enhanced Browser Storage Adapter
 * Advanced storage implementation with File System Access API, revision tracking with diffs,
 * and comprehensive export functionality
 */

import type {
  IStorageAdapter,
  StorageCapabilities,
  StorageFile,
  StorageFilter,
  SearchResult,
  StorageError,
} from './types';
import { nanoid } from 'nanoid';
import lunr from 'lunr';
import JSZip from 'jszip';
import * as diff from 'diff';

// Extend window interface for File System Access API
declare global {
  interface Window {
    showDirectoryPicker(options?: {
      mode?: 'read' | 'readwrite';
      startIn?:
        | 'desktop'
        | 'documents'
        | 'downloads'
        | 'music'
        | 'pictures'
        | 'videos';
    }): Promise<FileSystemDirectoryHandle>;
  }
}

// Extended storage file with revision history
export interface StorageFileWithRevisions extends StorageFile {
  revisions?: FileRevision[];
}

export interface FileRevision {
  id: string;
  fileId: string;
  revisionNumber: number;
  content: string;
  diff?: string; // Stored as JSON string of Change[]
  createdAt: string;
  author?: string;
  message?: string;
  size: number;
}

export interface ExportOptions {
  format: 'zip' | 'folder' | 'json';
  includeRevisions?: boolean;
  includeMetadata?: boolean;
  filter?: StorageFilter;
}

export interface ImportOptions {
  overwrite?: boolean;
  mergeStrategy?: 'overwrite' | 'skip' | 'rename';
  preserveIds?: boolean;
}

export class EnhancedBrowserStorageAdapter implements IStorageAdapter {
  private db: IDBDatabase | null = null;
  private searchIndex: lunr.Index | null = null;
  private fileSystemHandle: FileSystemDirectoryHandle | null = null;
  private hasFileSystemAccess = false;
  private revisionRetentionDays = 30; // Keep revisions for 30 days by default
  private maxRevisionsPerFile = 50; // Maximum number of revisions to keep

  getCapabilities(): StorageCapabilities {
    const hasFileSystemAPI = 'showDirectoryPicker' in window;
    // const hasFileHandleAPI = 'showSaveFilePicker' in window;

    return {
      canRequestDirectory: hasFileSystemAPI,
      canPersistHandles: hasFileSystemAPI && 'StorageManager' in window,
      supportsFileSystemAPI: hasFileSystemAPI,
      supportsIndexedDB: 'indexedDB' in window,
      supportsTauri: false,
      maxFileSize: 100 * 1024 * 1024, // 100MB
      supportedFileTypes: [
        'text/plain',
        'text/markdown',
        'application/json',
        'text/html',
      ],
      type: this.hasFileSystemAccess ? 'filesystem' : 'browser',
      supportsSync: hasFileSystemAPI,
      supportsRevisions: true,
      supportsBatchOperations: true,
    };
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Check IndexedDB availability
      const testDB = await this.openDatabase();
      testDB.close();
      return true;
    } catch {
      return false;
    }
  }

  async hasPermission(): Promise<boolean> {
    if (!this.fileSystemHandle) return true; // IndexedDB doesn't need permission

    const fileSystemHandle = this.fileSystemHandle;
    try {
      // Modern permission check
      if ('queryPermission' in fileSystemHandle) {
        const permission = await (fileSystemHandle as any).queryPermission({
          mode: 'readwrite',
        });
        return permission === 'granted';
      }

      // Fallback: try to create a test file
      try {
        await fileSystemHandle.getFileHandle('.phrasewright-test', {
          create: true,
        });
        await fileSystemHandle.removeEntry('.phrasewright-test');
        return true;
      } catch {
        return false;
      }
    } catch {
      return true; // If we can't check, assume we have permission (IndexedDB)
    }
  }

  async requestPermission(): Promise<boolean> {
    const capabilities = this.getCapabilities();

    if (capabilities.canRequestDirectory) {
      try {
        // Show directory picker
        this.fileSystemHandle = await window.showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents',
        });

        this.hasFileSystemAccess = true;

        // Create app structure in the selected directory
        await this.initializeFileSystemStructure();

        // Persist the handle for future sessions
        await this.persistFileSystemHandle(this.fileSystemHandle);

        return true;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled
          return false;
        }

        // Log error but fall back to IndexedDB
        console.warn(
          'File System Access failed, falling back to IndexedDB:',
          error
        );
      }
    }

    // Fall back to IndexedDB (no permission needed)
    return true;
  }

  async initialize(): Promise<void> {
    // Open IndexedDB
    this.db = await this.openDatabase();

    // Try to restore FileSystem handle from previous session
    await this.restoreFileSystemHandle();

    // Build search index
    await this.rebuildSearchIndex();

    // Clean up old revisions
    await this.cleanupOldRevisions();
  }

  async cleanup(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.searchIndex = null;
    this.fileSystemHandle = null;
    this.hasFileSystemAccess = false;
  }

  // Enhanced CRUD Operations with revision tracking
  async getFile(
    id: string,
    includeRevisions?: boolean
  ): Promise<StorageFileWithRevisions> {
    const db = await this.ensureDatabase();

    const file = await this.getFileFromDB(db, id);

    if (includeRevisions) {
      const revisions = await this.getFileRevisions(id);
      return { ...file, revisions };
    }

    return file;
  }

  async getFiles(filter?: StorageFilter): Promise<StorageFile[]> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readonly');
      const request = transaction.objectStore('files').getAll();

      request.onsuccess = () => {
        let files = request.result || [];

        // Apply filters
        if (filter) {
          if (filter.projectId) {
            files = files.filter((f) => f.projectId === filter.projectId);
          }
          if (filter.type) {
            files = files.filter((f) => f.type === filter.type);
          }
          if (filter.tags?.length) {
            files = files.filter((f) =>
              filter.tags!.some((tag) => f.tags.includes(tag))
            );
          }
          if (filter.createdAfter) {
            files = files.filter((f) => f.createdAt > filter.createdAfter!);
          }
          if (filter.updatedAfter) {
            files = files.filter((f) => f.updatedAt > filter.updatedAfter!);
          }
          if (filter.search) {
            // Use search index for text search
            const searchResults = this.searchIndex?.search(filter.search) || [];
            const searchIds = new Set(searchResults.map((r) => r.ref));
            files = files.filter((f) => searchIds.has(f.id));
          }
        }

        resolve(files);
      };

      request.onerror = () => reject(request.error);
    });
  }

  async createFile(
    file: Omit<StorageFile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<StorageFile> {
    const db = await this.ensureDatabase();

    const newFile: StorageFile = {
      ...file,
      id: nanoid(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      revision: 1,
    };

    // Save to IndexedDB
    await this.saveFileToDBbakc(db, newFile);

    // Create initial revision
    await this.createRevision(
      newFile.id,
      '',
      newFile.content,
      'Initial version'
    );

    // Update search index
    await this.addToSearchIndex(newFile);

    // Sync to file system if available
    if (this.hasFileSystemAccess && this.fileSystemHandle) {
      await this.syncToFileSystem(newFile);
    }

    return newFile;
  }

  async updateFile(
    id: string,
    updates: Partial<StorageFile>
  ): Promise<StorageFile> {
    const db = await this.ensureDatabase();
    const existingFile = await this.getFileFromDB(db, id);

    // Create revision if content changed
    if (updates.content && updates.content !== existingFile.content) {
      await this.createRevision(
        id,
        existingFile.content,
        updates.content,
        `Revision ${(existingFile.revision || 0) + 1}`
      );
    }

    const updatedFile: StorageFile = {
      ...existingFile,
      ...updates,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString(),
      revision: (existingFile.revision || 0) + 1,
    };

    // Save to IndexedDB
    await this.saveFileToDBbakc(db, updatedFile);

    // Update search index
    await this.rebuildSearchIndex();

    // Sync to file system if available
    if (this.hasFileSystemAccess && this.fileSystemHandle) {
      await this.syncToFileSystem(updatedFile);
    }

    return updatedFile;
  }

  async deleteFile(id: string): Promise<void> {
    const db = await this.ensureDatabase();

    // Delete file
    await this.deleteFileFromDB(db, id);

    // Delete revisions
    await this.deleteFileRevisions(id);

    // Update search index
    await this.rebuildSearchIndex();

    // Delete from file system if available
    if (this.hasFileSystemAccess && this.fileSystemHandle) {
      await this.deleteFromFileSystem(id);
    }
  }

  // Batch operations
  async createFiles(
    files: Array<Omit<StorageFile, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<StorageFile[]> {
    const db = await this.ensureDatabase();
    const transaction = db.transaction(['files', 'revisions'], 'readwrite');
    const fileStore = transaction.objectStore('files');
    const revisionStore = transaction.objectStore('revisions');

    const results: StorageFile[] = [];

    for (const file of files) {
      const newFile: StorageFile = {
        ...file,
        id: nanoid(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        revision: 1,
      };

      fileStore.add(newFile);

      // Create initial revision
      const revision: FileRevision = {
        id: nanoid(),
        fileId: newFile.id,
        revisionNumber: 1,
        content: newFile.content,
        createdAt: newFile.createdAt,
        message: 'Initial version',
        size: new Blob([newFile.content]).size,
      };

      revisionStore.add(revision);
      results.push(newFile);
    }

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });

    // Rebuild search index after batch operation
    await this.rebuildSearchIndex();

    // Sync to file system if available
    if (this.hasFileSystemAccess && this.fileSystemHandle) {
      for (const file of results) {
        await this.syncToFileSystem(file);
      }
    }

    return results;
  }

  async updateFiles(
    updates: Array<{ id: string; updates: Partial<StorageFile> }>
  ): Promise<StorageFile[]> {
    const results: StorageFile[] = [];

    for (const update of updates) {
      results.push(await this.updateFile(update.id, update.updates));
    }

    return results;
  }

  async deleteFiles(ids: string[]): Promise<void> {
    const db = await this.ensureDatabase();
    const transaction = db.transaction(['files', 'revisions'], 'readwrite');
    const fileStore = transaction.objectStore('files');
    const revisionStore = transaction.objectStore('revisions');

    for (const id of ids) {
      fileStore.delete(id);

      // Delete associated revisions
      const revisionIndex = revisionStore.index('fileId');
      const revisionRequest = revisionIndex.getAll(id);

      revisionRequest.onsuccess = () => {
        const revisions = revisionRequest.result || [];
        revisions.forEach((rev) => revisionStore.delete(rev.id));
      };
    }

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });

    // Update search index
    await this.rebuildSearchIndex();

    // Delete from file system if available
    if (this.hasFileSystemAccess && this.fileSystemHandle) {
      for (const id of ids) {
        await this.deleteFromFileSystem(id);
      }
    }
  }

  // Search
  async search(
    query: string,
    options?: { limit?: number; offset?: number }
  ): Promise<SearchResult[]> {
    if (!this.searchIndex) {
      await this.rebuildSearchIndex();
    }

    if (!this.searchIndex) {
      return [];
    }

    const results = this.searchIndex.search(query);
    const limit = options?.limit || 20;
    const offset = options?.offset || 0;

    const searchResults: SearchResult[] = [];

    for (let i = offset; i < Math.min(results.length, offset + limit); i++) {
      const result = results[i];
      const file = await this.getFile(result.ref);

      // Find match positions for highlighting
      const matches = this.findMatchPositions(file.content, query);

      searchResults.push({
        fileId: file.id,
        fileName: file.name,
        excerpt: this.generateExcerpt(file.content, query),
        score: result.score,
        matches,
      });
    }

    return searchResults;
  }

  async rebuildSearchIndex(): Promise<void> {
    const files = await this.getFiles();

    this.searchIndex = lunr(function () {
      this.ref('id');
      this.field('name', { boost: 10 });
      this.field('content');
      this.field('tags', { boost: 5 });
      this.metadataWhitelist = ['position'];

      files.forEach((file) => {
        this.add({
          id: file.id,
          name: file.name,
          content: file.content,
          tags: file.tags.join(' '),
        });
      });
    });
  }

  // Enhanced Export/Import
  async exportData(options?: ExportOptions): Promise<Blob> {
    const format = options?.format || 'zip';
    const files = await this.getFiles(options?.filter);

    switch (format) {
      case 'zip':
        return this.exportAsZip(files, options);
      case 'folder':
        return this.exportToFolder(files, options);
      case 'json':
      default:
        return this.exportAsJSON(files, options);
    }
  }

  private async exportAsZip(
    files: StorageFile[],
    options?: ExportOptions
  ): Promise<Blob> {
    const zip = new JSZip();

    // Create folder structure
    const documentsFolder = zip.folder('documents');
    const phrasesFolder = zip.folder('phrases');
    const metadataFolder = zip.folder('.metadata');

    for (const file of files) {
      const folder = file.type === 'document' ? documentsFolder : phrasesFolder;

      // Save main content
      folder?.file(`${file.name}.md`, file.content);

      // Save metadata if requested
      if (options?.includeMetadata) {
        const metadata = {
          id: file.id,
          tags: file.tags,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          revision: file.revision,
          metadata: file.metadata,
        };
        metadataFolder?.file(
          `${file.id}.json`,
          JSON.stringify(metadata, null, 2)
        );
      }

      // Save revisions if requested
      if (options?.includeRevisions) {
        const revisions = await this.getFileRevisions(file.id);
        if (revisions.length > 0) {
          const revisionsFolder = metadataFolder?.folder(
            `revisions/${file.id}`
          );
          revisions.forEach((rev, index) => {
            revisionsFolder?.file(
              `revision-${index + 1}.json`,
              JSON.stringify(rev, null, 2)
            );
          });
        }
      }
    }

    // Add manifest
    const manifest = {
      version: '2.0.0',
      exportDate: new Date().toISOString(),
      fileCount: files.length,
      includesRevisions: options?.includeRevisions || false,
      includesMetadata: options?.includeMetadata || false,
    };
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));

    return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  }

  private async exportToFolder(
    files: StorageFile[],
    options?: ExportOptions
  ): Promise<Blob> {
    if (!this.hasFileSystemAccess || !this.fileSystemHandle) {
      // Fall back to ZIP if no file system access
      return this.exportAsZip(files, options);
    }

    try {
      // Request a directory to export to
      const exportHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads',
      });

      // Create folder structure
      const documentsFolder = await exportHandle.getDirectoryHandle(
        'documents',
        {
          create: true,
        }
      );
      const phrasesFolder = await exportHandle.getDirectoryHandle('phrases', {
        create: true,
      });

      for (const file of files) {
        const folder =
          file.type === 'document' ? documentsFolder : phrasesFolder;

        // Save file
        const fileHandle = await folder.getFileHandle(`${file.name}.md`, {
          create: true,
        });
        const writable = await fileHandle.createWritable();
        await writable.write(file.content);
        await writable.close();
      }

      // Create success message as blob
      return new Blob(
        [
          `Export successful: ${files.length} files exported to selected folder`,
        ],
        { type: 'text/plain' }
      );
    } catch (error) {
      // Fall back to ZIP on error
      console.warn('Folder export failed, falling back to ZIP:', error);
      return this.exportAsZip(files, options);
    }
  }

  private async exportAsJSON(
    files: StorageFile[],
    options?: ExportOptions
  ): Promise<Blob> {
    const exportData: any = {
      version: '2.0.0',
      exportDate: new Date().toISOString(),
      files,
    };

    if (options?.includeRevisions) {
      const revisionsMap: Record<string, FileRevision[]> = {};

      for (const file of files) {
        const revisions = await this.getFileRevisions(file.id);
        if (revisions.length > 0) {
          revisionsMap[file.id] = revisions;
        }
      }

      exportData.revisions = revisionsMap;
    }

    return new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
  }

  async importData(data: Blob, options?: ImportOptions): Promise<void> {
    const text = await data.text();

    // Try to detect format
    if (text.startsWith('PK')) {
      // ZIP file
      await this.importFromZip(data, options);
    } else {
      // JSON
      await this.importFromJSON(text, options);
    }
  }

  private async importFromJSON(
    text: string,
    options?: ImportOptions
  ): Promise<void> {
    const importData = JSON.parse(text);
    const files = importData.files || [];
    const revisions = importData.revisions || {};

    for (const file of files) {
      const existingFile = await this.findFileByName(file.name);

      if (existingFile) {
        if (options?.mergeStrategy === 'skip') {
          continue;
        } else if (options?.mergeStrategy === 'rename') {
          file.name = this.generateUniqueName(file.name);
        }
        // Otherwise overwrite
      }

      // Create or update file
      const fileData = { ...file };
      if (!options?.preserveIds) {
        delete fileData.id;
      }
      delete fileData.createdAt;
      delete fileData.updatedAt;

      const newFile = await this.createFile(fileData);

      // Import revisions if available
      if (revisions[file.id]) {
        for (const rev of revisions[file.id]) {
          await this.importRevision(newFile.id, rev);
        }
      }
    }
  }

  private async importFromZip(
    blob: Blob,
    options?: ImportOptions
  ): Promise<void> {
    const zip = await JSZip.loadAsync(blob);

    // Read manifest if exists
    // const manifestFile = zip.file('manifest.json');
    // const manifest = manifestFile
    //   ? JSON.parse(await manifestFile.async('string'))
    //   : null;

    // Import documents
    const documentsFolder = zip.folder('documents');
    if (documentsFolder) {
      const files = documentsFolder.file(/.+\.md$/);
      for (const file of files) {
        const content = await file.async('string');
        const name = file.name.replace(/^documents\//, '').replace(/\.md$/, '');

        await this.importFile(
          {
            name,
            content,
            type: 'document',
            tags: [],
          },
          options
        );
      }
    }

    // Import phrases
    const phrasesFolder = zip.folder('phrases');
    if (phrasesFolder) {
      const files = phrasesFolder.file(/.+\.md$/);
      for (const file of files) {
        const content = await file.async('string');
        const name = file.name.replace(/^phrases\//, '').replace(/\.md$/, '');

        await this.importFile(
          {
            name,
            content,
            type: 'phrase',
            tags: [],
          },
          options
        );
      }
    }
  }

  // Revision management
  private async createRevision(
    fileId: string,
    oldContent: string,
    newContent: string,
    message?: string
  ): Promise<void> {
    const db = await this.ensureDatabase();

    // Calculate diff
    const changes = diff.diffLines(oldContent, newContent);
    const diffString = JSON.stringify(changes);

    // Get current revision count
    const revisions = await this.getFileRevisions(fileId);
    const revisionNumber = revisions.length + 1;

    const revision: FileRevision = {
      id: nanoid(),
      fileId,
      revisionNumber,
      content: newContent,
      diff: diffString,
      createdAt: new Date().toISOString(),
      message,
      size: new Blob([newContent]).size,
    };

    // Save revision
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(['revisions'], 'readwrite');
      const request = transaction.objectStore('revisions').add(revision);

      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error);
    });

    // Clean up old revisions if needed
    await this.cleanupFileRevisions(fileId);
  }

  private async getFileRevisions(fileId: string): Promise<FileRevision[]> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['revisions'], 'readonly');
      const index = transaction.objectStore('revisions').index('fileId');
      const request = index.getAll(fileId);

      request.onsuccess = () => {
        const revisions = request.result || [];
        // Sort by revision number descending
        revisions.sort((a, b) => b.revisionNumber - a.revisionNumber);
        resolve(revisions);
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async deleteFileRevisions(fileId: string): Promise<void> {
    const db = await this.ensureDatabase();
    const revisions = await this.getFileRevisions(fileId);

    const transaction = db.transaction(['revisions'], 'readwrite');
    const store = transaction.objectStore('revisions');

    for (const revision of revisions) {
      store.delete(revision.id);
    }

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  private async cleanupFileRevisions(fileId: string): Promise<void> {
    const revisions = await this.getFileRevisions(fileId);

    if (revisions.length <= this.maxRevisionsPerFile) {
      return;
    }

    // Keep only the most recent revisions
    const toDelete = revisions.slice(this.maxRevisionsPerFile);
    const db = await this.ensureDatabase();
    const transaction = db.transaction(['revisions'], 'readwrite');
    const store = transaction.objectStore('revisions');

    for (const revision of toDelete) {
      store.delete(revision.id);
    }

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  private async cleanupOldRevisions(): Promise<void> {
    const db = await this.ensureDatabase();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.revisionRetentionDays);
    const cutoffString = cutoffDate.toISOString();

    const transaction = db.transaction(['revisions'], 'readwrite');
    const store = transaction.objectStore('revisions');
    const index = store.index('createdAt');
    const range = IDBKeyRange.upperBound(cutoffString);
    const request = index.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Private helper methods
  private async ensureDatabase(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = await this.openDatabase();
    }
    return this.db;
  }

  private async openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('phrasewright-enhanced-storage', 3);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Files store
        if (!db.objectStoreNames.contains('files')) {
          const fileStore = db.createObjectStore('files', { keyPath: 'id' });
          fileStore.createIndex('type', 'type', { unique: false });
          fileStore.createIndex('name', 'name', { unique: false });
          fileStore.createIndex('createdAt', 'createdAt', { unique: false });
          fileStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          fileStore.createIndex('projectId', 'projectId', { unique: false });
        }

        // Revisions store
        if (!db.objectStoreNames.contains('revisions')) {
          const revisionStore = db.createObjectStore('revisions', {
            keyPath: 'id',
          });
          revisionStore.createIndex('fileId', 'fileId', { unique: false });
          revisionStore.createIndex('createdAt', 'createdAt', {
            unique: false,
          });
          revisionStore.createIndex('revisionNumber', 'revisionNumber', {
            unique: false,
          });
        }

        // Handles store for File System API
        if (!db.objectStoreNames.contains('handles')) {
          db.createObjectStore('handles');
        }

        // Sync metadata store
        if (!db.objectStoreNames.contains('sync')) {
          const syncStore = db.createObjectStore('sync', { keyPath: 'id' });
          syncStore.createIndex('fileId', 'fileId', { unique: true });
          syncStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        }
      };
    });
  }

  private async getFileFromDB(
    db: IDBDatabase,
    id: string
  ): Promise<StorageFile> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readonly');
      const request = transaction.objectStore('files').get(id);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          const error: StorageError = Object.assign(
            new Error(`File not found: ${id}`),
            {
              code: 'NOT_FOUND' as const,
              retryable: false,
              fallbackAvailable: false,
            }
          );
          reject(error);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async saveFileToDBbakc(
    db: IDBDatabase,
    file: StorageFile
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readwrite');
      const request = transaction.objectStore('files').put(file);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async deleteFileFromDB(db: IDBDatabase, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readwrite');
      const request = transaction.objectStore('files').delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async findFileByName(name: string): Promise<StorageFile | null> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readonly');
      const index = transaction.objectStore('files').index('name');
      const request = index.get(name);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private generateUniqueName(baseName: string): string {
    const timestamp = Date.now();
    const extension = baseName.includes('.')
      ? baseName.substring(baseName.lastIndexOf('.'))
      : '';
    const nameWithoutExt = baseName.replace(extension, '');
    return `${nameWithoutExt}_${timestamp}${extension}`;
  }

  private async importFile(
    fileData: Omit<StorageFile, 'id' | 'createdAt' | 'updatedAt'>,
    options?: ImportOptions
  ): Promise<void> {
    const existingFile = await this.findFileByName(fileData.name);

    if (existingFile) {
      if (options?.mergeStrategy === 'skip') {
        return;
      } else if (options?.mergeStrategy === 'rename') {
        fileData.name = this.generateUniqueName(fileData.name);
      } else if (options?.overwrite) {
        await this.updateFile(existingFile.id, fileData);
        return;
      }
    }

    await this.createFile(fileData);
  }

  private async importRevision(
    fileId: string,
    revision: FileRevision
  ): Promise<void> {
    const db = await this.ensureDatabase();

    const newRevision: FileRevision = {
      ...revision,
      id: nanoid(),
      fileId,
    };

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(['revisions'], 'readwrite');
      const request = transaction.objectStore('revisions').add(newRevision);

      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error);
    });
  }

  // File System API integration
  private async initializeFileSystemStructure(): Promise<void> {
    if (!this.fileSystemHandle) return;

    try {
      // Create app directories
      await this.fileSystemHandle.getDirectoryHandle('documents', {
        create: true,
      });
      await this.fileSystemHandle.getDirectoryHandle('phrases', {
        create: true,
      });
      await this.fileSystemHandle.getDirectoryHandle('.metadata', {
        create: true,
      });

      // Create config file
      const configHandle = await this.fileSystemHandle.getFileHandle(
        '.phrasewright',
        {
          create: true,
        }
      );
      const writable = await configHandle.createWritable();
      await writable.write(
        JSON.stringify({
          version: '2.0.0',
          createdAt: new Date().toISOString(),
        })
      );
      await writable.close();
    } catch (error) {
      console.warn('Failed to initialize file system structure:', error);
    }
  }

  private async persistFileSystemHandle(
    handle: FileSystemDirectoryHandle
  ): Promise<void> {
    const db = await this.ensureDatabase();

    // Also try to persist using browser storage manager
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        const persistent = await navigator.storage.persist();
        if (persistent) {
          console.log('Storage will persist');
        }
      } catch (error) {
        console.warn('Failed to persist storage:', error);
      }
    }

    return new Promise((resolve) => {
      const transaction = db.transaction(['handles'], 'readwrite');
      const request = transaction
        .objectStore('handles')
        .put(handle, 'directory');

      request.onsuccess = () => resolve();
      request.onerror = () => resolve(); // Don't fail, just warn
    });
  }

  private async restoreFileSystemHandle(): Promise<void> {
    const db = await this.ensureDatabase();

    return new Promise((resolve) => {
      const transaction = db.transaction(['handles'], 'readonly');
      const request = transaction.objectStore('handles').get('directory');

      request.onsuccess = async () => {
        if (request.result) {
          this.fileSystemHandle = request.result as FileSystemDirectoryHandle;
          this.hasFileSystemAccess = await this.hasPermission();
        }
        resolve();
      };

      request.onerror = () => resolve(); // Don't fail
    });
  }

  private async syncToFileSystem(file: StorageFile): Promise<void> {
    if (!this.fileSystemHandle) return;

    try {
      const folder = file.type === 'document' ? 'documents' : 'phrases';
      const dirHandle = await this.fileSystemHandle.getDirectoryHandle(folder, {
        create: true,
      });

      // Save content file
      const fileHandle = await dirHandle.getFileHandle(`${file.name}.md`, {
        create: true,
      });
      const writable = await fileHandle.createWritable();
      await writable.write(file.content);
      await writable.close();

      // Save metadata
      const metadataDir = await this.fileSystemHandle.getDirectoryHandle(
        '.metadata',
        {
          create: true,
        }
      );
      const metadataHandle = await metadataDir.getFileHandle(
        `${file.id}.json`,
        {
          create: true,
        }
      );
      const metadataWritable = await metadataHandle.createWritable();
      await metadataWritable.write(
        JSON.stringify(
          {
            id: file.id,
            name: file.name,
            type: file.type,
            tags: file.tags,
            metadata: file.metadata,
            createdAt: file.createdAt,
            updatedAt: file.updatedAt,
            revision: file.revision,
          },
          null,
          2
        )
      );
      await metadataWritable.close();

      // Update sync metadata
      await this.updateSyncMetadata(file.id);
    } catch (error) {
      console.warn('Failed to sync to file system:', error);
      // Don't throw - IndexedDB is our source of truth
    }
  }

  private async deleteFromFileSystem(id: string): Promise<void> {
    if (!this.fileSystemHandle) return;

    try {
      // Find and delete the file
      const file = await this.getFile(id);
      const folder = file.type === 'document' ? 'documents' : 'phrases';
      const dirHandle = await this.fileSystemHandle.getDirectoryHandle(folder);

      await dirHandle.removeEntry(`${file.name}.md`);

      // Delete metadata
      const metadataDir =
        await this.fileSystemHandle.getDirectoryHandle('.metadata');
      await metadataDir.removeEntry(`${id}.json`);
    } catch (error) {
      console.warn('Failed to delete from file system:', error);
      // Don't throw - IndexedDB is our source of truth
    }
  }

  private async updateSyncMetadata(fileId: string): Promise<void> {
    const db = await this.ensureDatabase();

    const syncData = {
      id: nanoid(),
      fileId,
      lastSynced: new Date().toISOString(),
      syncStatus: 'success',
    };

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(['sync'], 'readwrite');
      const store = transaction.objectStore('sync');
      const index = store.index('fileId');

      // Check if sync record exists
      const getRequest = index.get(fileId);

      getRequest.onsuccess = () => {
        if (getRequest.result) {
          // Update existing
          store.put({
            ...getRequest.result,
            ...syncData,
            id: getRequest.result.id,
          });
        } else {
          // Create new
          store.add(syncData);
        }
      };

      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  private async addToSearchIndex(_file: StorageFile): Promise<void> {
    // Lunr doesn't support dynamic updates well, so we rebuild
    await this.rebuildSearchIndex();
  }

  private findMatchPositions(
    content: string,
    query: string
  ): Array<{ field: string; indices: Array<[number, number]> }> {
    const matches: Array<{ field: string; indices: Array<[number, number]> }> =
      [];
    const queryLower = query.toLowerCase();
    const contentLower = content.toLowerCase();

    let index = 0;
    while ((index = contentLower.indexOf(queryLower, index)) !== -1) {
      matches.push({
        field: 'content',
        indices: [[index, index + query.length]],
      });
      index += query.length;
    }

    return matches;
  }

  private generateExcerpt(content: string, query: string): string {
    const queryLower = query.toLowerCase();
    const contentLower = content.toLowerCase();
    const index = contentLower.indexOf(queryLower);

    if (index === -1) {
      return content.substring(0, 150) + (content.length > 150 ? '...' : '');
    }

    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + query.length + 100);

    let excerpt = content.substring(start, end);
    if (start > 0) excerpt = '...' + excerpt;
    if (end < content.length) excerpt = excerpt + '...';

    return excerpt;
  }
}
